import { Controller } from "@hotwired/stimulus";
import "./component.scss";
export default class extends Controller {
  static targets = ["slider", "dot"]; // "dot" hinzugefügt
  static values = {
    cloneCount: { type: Number, default: 2 },
    // Neue Value für die aktive Dot-Klasse
    dotActiveClass: { type: String, default: "header__dot--active" },
  };

  connect() {
    this.hasInitialized = false;
    this.slideWidth = 0;
    this.isTransitioning = false;
    this.isManualScrolling = false;

    // Event-Bindings (using bound functions for consistent `this`)
    this.onScrollHandler = this.onManualScroll.bind(this);
    this.onMouseEnterHandler = this.onMouseEnter.bind(this);
    this.onMouseLeaveHandler = this.onMouseLeave.bind(this);
    this.onResizeHandler = this.updateSlideWidthAndPosition.bind(this); // Wird auch die Dot-States aktualisieren

    this.sliderTarget.addEventListener("scroll", this.onScrollHandler);
    this.sliderTarget.addEventListener("mouseenter", this.onMouseEnterHandler);
    this.sliderTarget.addEventListener("mouseleave", this.onMouseLeaveHandler);
    window.addEventListener("resize", this.onResizeHandler);

    this.setupInfiniteScroll();
    this.startAutoplay();

    // Initialer Aufruf für die Punkte, nachdem der Slider initialisiert ist
    // Muss nach setupInfiniteScroll() aufgerufen werden, da sonst slideWidth evtl. noch 0 ist.
    this.updateDotStates();
  }

  disconnect() {
    this.stopAutoplay();

    this.sliderTarget.removeEventListener("scroll", this.onScrollHandler);
    this.sliderTarget.removeEventListener(
      "mouseenter",
      this.onMouseEnterHandler
    );
    this.sliderTarget.removeEventListener(
      "mouseleave",
      this.onMouseLeaveHandler
    );
    window.removeEventListener("resize", this.onResizeHandler);
  }

  setupInfiniteScroll() {
    if (this.hasInitialized) return;

    this.setupClones();
    this.updateSlideWidth();
    this.initialPositionSlider();

    this.lastScrollTime = Date.now();
    this.isHovered = false;
    this.hasInitialized = true;
  }

  setupClones() {
    const slides = Array.from(this.sliderTarget.children).filter(
      (child) => child.dataset.clone !== "true" // Exclude existing clones
    );

    if (slides.length === 0) {
      console.warn("No real slides found to clone.");
      return;
    }

    if (slides.length < 2) {
      console.warn(
        "Not enough real slides for infinite loop. Need at least 2."
      );
      return;
    }

    // Remove any existing clones before setting up new ones to prevent duplicates
    Array.from(this.sliderTarget.children)
      .filter((child) => child.dataset.clone === "true")
      .forEach((clone) => clone.remove());

    for (let i = 0; i < this.cloneCountValue; i++) {
      const slideToClone = slides[slides.length - 1 - i];
      if (slideToClone) {
        const clonedNode = slideToClone.cloneNode(true);
        clonedNode.dataset.clone = "true";
        this.sliderTarget.insertBefore(
          clonedNode,
          this.sliderTarget.firstElementChild
        );
      }
    }

    for (let i = 0; i < this.cloneCountValue; i++) {
      const slideToClone = slides[i];
      if (slideToClone) {
        const clonedNode = slideToClone.cloneNode(true);
        clonedNode.dataset.clone = "true";
        this.sliderTarget.appendChild(clonedNode);
      }
    }

    this.realSlidesCount = slides.length;
    this.totalSlidesCount = Array.from(this.sliderTarget.children).length;
  }

  updateSlideWidth() {
    const firstRealSlide = Array.from(this.sliderTarget.children).find(
      (child) => child.dataset.clone !== "true"
    );

    if (firstRealSlide) {
      const style = getComputedStyle(firstRealSlide);
      this.slideWidth =
        firstRealSlide.offsetWidth +
        parseFloat(style.marginLeft) +
        parseFloat(style.marginRight);

      if (isNaN(this.slideWidth) || this.slideWidth <= 0) {
        console.warn(
          "Calculated slideWidth is invalid. Falling back to sliderTarget.clientWidth."
        );
        this.slideWidth = this.sliderTarget.clientWidth;
      }
    } else {
      console.warn(
        "No real slides found to calculate slideWidth. Falling back to sliderTarget.clientWidth."
      );
      this.slideWidth = this.sliderTarget.clientWidth;
    }
  }

  updateSlideWidthAndPosition() {
    this.updateSlideWidth();
    // Only reposition if slider is not actively being scrolled by user
    if (!this.isManualScrolling) {
      this.initialPositionSlider();
    }
    // Nach dem Resizen auch die Punkte aktualisieren
    this.updateDotStates();
  }

  initialPositionSlider() {
    requestAnimationFrame(() => {
      this.sliderTarget.style.scrollBehavior = "auto";
      // eslint-disable-next-line no-unused-vars
      const forceReflow = this.sliderTarget.offsetHeight; // Force reflow to apply style immediately
      this.sliderTarget.scrollLeft = this.cloneCountValue * this.slideWidth;

      // Re-enable smooth scroll after positioning
      setTimeout(() => {
        this.sliderTarget.style.scrollBehavior = "smooth";
        this.updateDotStates(); // Punkte auch nach initialer Positionierung aktualisieren
      }, 0);
    });
  }

  onManualScroll() {
    this.lastScrollTime = Date.now();
    this.isManualScrolling = true;
    clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => {
      this.isManualScrolling = false;
      this.checkLoop();
      this.updateDotStates(); // Punkte aktualisieren, wenn der manuelle Scrollvorgang endet
    }, 150);
    this.updateDotStates(); // Punkte auch während des Scrollens aktualisieren, für flüssigere Anzeige
  }

  onMouseEnter() {
    this.isHovered = true;
  }

  onMouseLeave() {
    this.isHovered = false;
  }

  checkLoop() {
    if (
      this.isTransitioning ||
      !this.hasInitialized ||
      this.realSlidesCount < 2
    )
      return;

    const scrollLeft = this.sliderTarget.scrollLeft;
    const realContentWidth = this.realSlidesCount * this.slideWidth;
    const clonedContentWidth = this.cloneCountValue * this.slideWidth;

    const tolerance = Math.min(5, this.slideWidth * 0.05);

    if (scrollLeft >= clonedContentWidth + realContentWidth - tolerance) {
      this.performInstantJump(clonedContentWidth);
    } else if (scrollLeft <= clonedContentWidth - tolerance) {
      this.performInstantJump(
        clonedContentWidth + realContentWidth - this.sliderTarget.clientWidth
      );
    }
  }

  performInstantJump(targetScrollLeft) {
    if (this.isTransitioning) return;

    this.isTransitioning = true;
    this.sliderTarget.style.scrollBehavior = "auto";
    // eslint-disable-next-line no-unused-vars
    const forceReflow = this.sliderTarget.offsetHeight;

    this.sliderTarget.scrollLeft = targetScrollLeft;

    requestAnimationFrame(() => {
      this.sliderTarget.style.scrollBehavior = "smooth";
      this.isTransitioning = false;
      this.updateDotStates(); // Punkte auch nach einem sofortigen Sprung aktualisieren
    });
  }

  next() {
    this.stopAutoplay();
    this.sliderTarget.scrollTo({
      left: this.sliderTarget.scrollLeft + this.slideWidth,
      behavior: "smooth",
    });
    this.lastScrollTime = Date.now();
    this.startAutoplay();
    // Punkte aktualisieren nach dem Scrollen
    // Da es smooth ist, wird updateDotStates durch onManualScroll ausgelöst.
    // Ein sofortiger Aufruf könnte zu inkonsistentem Verhalten führen,
    // da der Scroll noch nicht abgeschlossen ist.
  }

  prev() {
    this.stopAutoplay();
    this.sliderTarget.scrollTo({
      left: this.sliderTarget.scrollLeft - this.slideWidth,
      behavior: "smooth",
    });
    this.lastScrollTime = Date.now();
    this.startAutoplay();
    // Punkte aktualisieren nach dem Scrollen
    // Siehe Kommentar bei next()
  }

  startAutoplay() {
    this.stopAutoplay();

    if (this.realSlidesCount < 2 || !this.hasInitialized) {
      console.warn(
        "Autoplay will not start: Not enough slides or not initialized."
      );
      return;
    }

    this.autoplayInterval = setInterval(() => {
      const now = Date.now();
      if (
        !this.isHovered &&
        !this.isManualScrolling &&
        !this.isTransitioning &&
        now - this.lastScrollTime > 3000
      ) {
        this.next();
        this.lastScrollTime = now;
        // Punkte nach automatischem Scrollen aktualisieren
        // Hier ist es wichtiger, weil onManualScroll nicht ausgelöst wird.
        setTimeout(() => this.updateDotStates(), 350); // Kleine Verzögerung, damit Scroll abgeschlossen ist
      }
    }, 1000);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  // --- Neue Methode für die Punkte-Logik ---
  updateDotStates() {
    if (this.slideWidth === 0 || !this.realSlidesCount) {
      return;
    }

    const scrollLeft = this.sliderTarget.scrollLeft;
    const clonedContentWidth = this.cloneCountValue * this.slideWidth;

    let effectiveScrollLeft = scrollLeft - clonedContentWidth;

    if (effectiveScrollLeft < 0) {
      effectiveScrollLeft += this.realSlidesCount * this.slideWidth;
    } else if (effectiveScrollLeft >= this.realSlidesCount * this.slideWidth) {
      effectiveScrollLeft -= this.realSlidesCount * this.slideWidth;
    }

    const activeRealSlideIndex = Math.floor(
      effectiveScrollLeft / this.slideWidth
    );

    // Alle Punkte deaktivieren
    this.dotTargets.forEach((dot) => {
      // Nutze die dotActiveClassValue, die wir aus dem HTML erhalten haben
      dot.classList.remove(this.dotActiveClassValue);
    });

    // Den entsprechenden Punkt aktivieren
    if (this.dotTargets[activeRealSlideIndex]) {
      // Nutze die dotActiveClassValue
      this.dotTargets[activeRealSlideIndex].classList.add(
        this.dotActiveClassValue
      );
    }
  }
}
